import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  SimpleGrid,
  Badge,
  Avatar,
  Flex,
  Icon,
  Progress,
  Tooltip,
  CircularProgress,
  CircularProgressLabel,
  Grid,
  GridItem,
  Button,
  Wrap,
  WrapItem,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Divider,
} from '@chakra-ui/react';
import {
  FaTasks,
  FaProjectDiagram,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUserShield,
  FaChartLine,
  FaTrophy,
  FaFire,
  FaHistory,
  FaUserCog,
  FaCalendarDay,
  FaCoins,
  FaArrowRight,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { AuthenticatedUser, useAuthenticatedUser } from '../../contexts/AuthenticatedUserInfoContext';
import { useTasks } from '../../hooks/useTasks';
import { useProjects } from '../../hooks/useProjects';
import { useClients } from '../../hooks/useClients';
import { Task } from '../../api/taskApi';
import { useUser } from '../../hooks/useUsers';

interface ProfileTabProps {
  authenticatedUser?: AuthenticatedUser | null;
}

type UserRef = string | { _id: string };

const getUserId = (ref?: UserRef | null) => {
  if (!ref) return '';
  return typeof ref === 'string' ? ref : ref._id;
};

const isUserInvolvedInTask = (task: Task, userId: string) => {
  const isAssigned = (task.assignedToUser || []).some((ref) => getUserId(ref as UserRef) === userId);
  const isCreator = getUserId(task.createdBy as UserRef) === userId;
  const isAuthorized = (task.authBy || []).some((ref) => getUserId(ref as UserRef) === userId);
  return isAssigned || isCreator || isAuthorized;
};

const ProfileTab: React.FC<ProfileTabProps> = ({ authenticatedUser: propAuthenticatedUser }) => {
  const navigate = useNavigate();
  const { authenticatedUser: contextAuthenticatedUser, isAuthenticated, loading: authLoading } =
    useAuthenticatedUser();

  const authenticatedUser = contextAuthenticatedUser || propAuthenticatedUser;

  let userId = authenticatedUser?.LoggedInUserId;
  if (!userId) {
    try {
      const localUser = JSON.parse(localStorage.getItem('user') || 'null');
      userId = localUser?._id || localUser?.LoggedInUserId;
    } catch {
      userId = '';
    }
  }

  const { data: fetchedUser, isLoading: userLoading, isError: userError } = useUser(userId || '');
  const displayUser = authenticatedUser || fetchedUser;

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const insetBg = useColorModeValue('gray.50', 'gray.700');
  const heroBg = useColorModeValue(
    'linear(to-br, blue.500, purple.600)',
    'linear(to-br, blue.600, purple.700)'
  );

  const { data: tasks, isLoading: tasksLoading } = useTasks();
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: clients, isLoading: clientsLoading } = useClients();

  const dataLoading = tasksLoading || projectsLoading || clientsLoading;

  const userStats = React.useMemo(() => {
    if (!authenticatedUser) return null;

    const allTasks = Array.isArray(tasks) ? tasks : [];
    const allProjects = Array.isArray(projects) ? projects : [];
    const allClients = Array.isArray(clients) ? clients : [];

    const userTasks = allTasks.filter((task) =>
      isUserInvolvedInTask(task, authenticatedUser.LoggedInUserId)
    );

    const completedTasks = userTasks.filter(
      (task) => task.status?.toLowerCase() === 'completed'
    );
    const pendingTasks = userTasks.filter((task) => {
      const status = task.status?.toLowerCase() || '';
      return status === 'in progress' || status === 'pending' || status === 'not started';
    });
    const urgentTasks = userTasks.filter((task) => {
      const priority = task.priority?.toLowerCase() || '';
      return priority === 'urgent' || priority === 'high';
    });
    const overdueTasks = userTasks.filter((task) => {
      if (!task.dueDate) return false;
      const dueDate = new Date(task.dueDate);
      return dueDate < new Date() && task.status?.toLowerCase() !== 'completed';
    });

    const userProjects = allProjects.filter((project) =>
      getUserId(project.userWhoCreatedProject as UserRef) === authenticatedUser.LoggedInUserId
    );

    const completedTasksWithDates = completedTasks.filter(
      (task) => task.dateStarted && task.updatedAt
    );
    const avgCompletionTime =
      completedTasksWithDates.length > 0
        ? Math.round(
            completedTasksWithDates.reduce((acc, task) => {
              const start = new Date(task.dateStarted!);
              const end = new Date(task.updatedAt);
              return acc + (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
            }, 0) / completedTasksWithDates.length
          )
        : 0;

    const totalTaskValue = userTasks.reduce((acc, task) => {
      const value = task.agreedPrice || task.quotedPrice || 0;
      return acc + (typeof value === 'number' ? value : 0);
    }, 0);

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const recentTasks = userTasks.filter((task) => {
      const taskDate = new Date(task.updatedAt || task.createdAt || new Date());
      return taskDate >= oneWeekAgo;
    });

    const completionRate =
      userTasks.length > 0 ? Math.round((completedTasks.length / userTasks.length) * 100) : 0;

    return {
      totalTasks: userTasks.length,
      completedTasks: completedTasks.length,
      pendingTasks: pendingTasks.length,
      urgentTasks: urgentTasks.length,
      overdueTasks: overdueTasks.length,
      completionRate,
      projectsCreated: userProjects.length,
      totalProjects: allProjects.length,
      totalClients: allClients.length,
      avgCompletionTime,
      totalTaskValue,
      recentActivity: recentTasks.length,
      productivity: completionRate,
    };
  }, [tasks, projects, clients, authenticatedUser]);

  const recentUserTasks = React.useMemo(() => {
    if (!authenticatedUser) return [];

    const allTasks = Array.isArray(tasks) ? tasks : [];
    return allTasks
      .filter((task) => isUserInvolvedInTask(task, authenticatedUser.LoggedInUserId))
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0);
        const dateB = new Date(b.updatedAt || b.createdAt || 0);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 6);
  }, [tasks, authenticatedUser]);

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'editor':
        return 'blue';
      case 'viewer':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return FaUserShield;
      case 'editor':
        return FaUserCog;
      default:
        return FaUsers;
    }
  };

  const formatJoinDate = (createdAt?: string) => {
    if (!createdAt) return 'Member';
    return `Member since ${new Date(createdAt).toLocaleDateString(undefined, {
      month: 'short',
      year: 'numeric',
    })}`;
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'green';
      case 'in progress':
        return 'blue';
      case 'pending':
        return 'orange';
      case 'on hold':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const blueIconBg = useColorModeValue('blue.50', 'blue.900');
  const greenIconBg = useColorModeValue('green.50', 'green.900');
  const orangeIconBg = useColorModeValue('orange.50', 'orange.900');
  const redIconBg = useColorModeValue('red.50', 'red.900');

  const StatCard: React.FC<{
    label: string;
    value: string | number;
    help: string;
    icon: React.ElementType;
    color: string;
    iconBg: string;
  }> = ({ label, value, help, icon, color, iconBg }) => (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      p={5}
      boxShadow="sm"
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
      h="full"
    >
      <Flex justify="space-between" align="start">
        <Box>
          <Text fontSize="xs" fontWeight="semibold" color={mutedText} textTransform="uppercase" letterSpacing="wide">
            {label}
          </Text>
          <Text fontSize="3xl" fontWeight="bold" color={`${color}.500`} lineHeight="1.1" mt={1}>
            {value}
          </Text>
          <Text fontSize="sm" color={mutedText} mt={1}>
            {help}
          </Text>
        </Box>
        <Flex
          w={10}
          h={10}
          borderRadius="lg"
          bg={iconBg}
          align="center"
          justify="center"
        >
          <Icon as={icon} color={`${color}.500`} boxSize={5} />
        </Flex>
      </Flex>
    </Box>
  );

  const SectionCard: React.FC<{
    title: string;
    icon: React.ElementType;
    children: React.ReactNode;
  }> = ({ title, icon, children }) => (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      p={{ base: 4, md: 5 }}
      boxShadow="sm"
    >
      <HStack spacing={2} mb={4}>
        <Icon as={icon} color="blue.500" />
        <Heading size="sm">{title}</Heading>
      </HStack>
      {children}
    </Box>
  );

  if (authLoading) {
    return (
      <Box bg={cardBg} borderRadius="2xl" p={8} border="1px solid" borderColor={borderColor}>
        <HStack spacing={6} mb={8}>
          <SkeletonCircle size="20" />
          <VStack align="start" flex={1} spacing={3}>
            <Skeleton height="28px" width="220px" />
            <Skeleton height="18px" width="180px" />
          </VStack>
        </HStack>
        <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height="110px" borderRadius="xl" />
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (!isAuthenticated && !authenticatedUser) {
    return (
      <Box bg={cardBg} borderRadius="2xl" p={10} textAlign="center" border="1px solid" borderColor={borderColor}>
        <Text color="red.500" fontWeight="medium">
          Please log in to view your profile.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        bgGradient={heroBg}
        borderRadius="2xl"
        p={{ base: 5, md: 8 }}
        mb={6}
        color="white"
        boxShadow="lg"
      >
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', lg: 'center' }}
          gap={6}
        >
          {userLoading ? (
            <HStack spacing={5}>
              <SkeletonCircle size="20" />
              <SkeletonText noOfLines={3} spacing={3} width="240px" />
            </HStack>
          ) : userError || !displayUser ? (
            <Text color="red.100">Failed to load profile information.</Text>
          ) : (
            <HStack spacing={5} align="center">
              <Avatar
                size="xl"
                name={`${displayUser.firstName || ''} ${displayUser.lastName || ''}`}
                bg="whiteAlpha.300"
                color="white"
                fontWeight="bold"
              />
              <VStack align="start" spacing={2}>
                <Heading size="lg" color="white">
                  {displayUser.firstName} {displayUser.lastName}
                </Heading>
                <Text opacity={0.9}>{displayUser.email}</Text>
                <Wrap spacing={2}>
                  <WrapItem>
                    <Badge
                      colorScheme={getRoleBadgeColor(displayUser.role || '')}
                      variant="solid"
                      borderRadius="full"
                      px={3}
                    >
                      <HStack spacing={1}>
                        <Icon as={getRoleIcon(displayUser.role || '')} boxSize={3} />
                        <Text>{displayUser.role?.toUpperCase()}</Text>
                      </HStack>
                    </Badge>
                  </WrapItem>
                  <WrapItem>
                    <Badge variant="outline" borderColor="whiteAlpha.500" borderRadius="full" px={3}>
                      {formatJoinDate((fetchedUser as any)?.createdAt)}
                    </Badge>
                  </WrapItem>
                </Wrap>
              </VStack>
            </HStack>
          )}

          <VStack spacing={2} align="center" alignSelf={{ base: 'center', lg: 'flex-end' }}>
            <CircularProgress
              value={userStats?.productivity || 0}
              color="white"
              trackColor="whiteAlpha.300"
              size="88px"
              thickness="8px"
            >
              <CircularProgressLabel color="white" fontWeight="bold" fontSize="md">
                {userStats?.productivity || 0}%
              </CircularProgressLabel>
            </CircularProgress>
            <HStack spacing={1} opacity={0.9}>
              <Icon as={FaTrophy} boxSize={3} />
              <Text fontSize="sm">Completion rate</Text>
            </HStack>
          </VStack>
        </Flex>
      </Box>

      <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4} mb={6}>
        <StatCard
          label="Total tasks"
          value={dataLoading ? '…' : userStats?.totalTasks || 0}
          help="Assigned or created by you"
          icon={FaTasks}
          color="blue"
          iconBg={blueIconBg}
        />
        <StatCard
          label="Completed"
          value={dataLoading ? '…' : userStats?.completedTasks || 0}
          help={`${userStats?.completionRate || 0}% completion rate`}
          icon={FaCheckCircle}
          color="green"
          iconBg={greenIconBg}
        />
        <StatCard
          label="In progress"
          value={dataLoading ? '…' : userStats?.pendingTasks || 0}
          help="Active workload"
          icon={FaClock}
          color="orange"
          iconBg={orangeIconBg}
        />
        <StatCard
          label="Urgent"
          value={dataLoading ? '…' : userStats?.urgentTasks || 0}
          help="High priority items"
          icon={FaExclamationTriangle}
          color="red"
          iconBg={redIconBg}
        />
      </SimpleGrid>

      <Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 340px' }} gap={6} alignItems="start">
        <GridItem>
          <VStack spacing={6} align="stretch">
            <SectionCard title="Productivity overview" icon={FaChartLine}>
              <VStack spacing={5} align="stretch">
                <Box>
                  <Flex justify="space-between" mb={2}>
                    <Text fontWeight="medium">Task completion</Text>
                    <Text fontSize="sm" color={mutedText}>
                      {userStats?.completedTasks || 0} of {userStats?.totalTasks || 0}
                    </Text>
                  </Flex>
                  <Progress
                    value={userStats?.completionRate || 0}
                    colorScheme="blue"
                    size="lg"
                    borderRadius="full"
                  />
                </Box>

                <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
                  <Box textAlign="center" p={4} bg={insetBg} borderRadius="lg">
                    <Icon as={FaProjectDiagram} color="blue.500" mb={2} />
                    <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                      {userStats?.projectsCreated || 0}
                    </Text>
                    <Text fontSize="sm" color={mutedText}>
                      Projects created
                    </Text>
                  </Box>
                  <Box textAlign="center" p={4} bg={insetBg} borderRadius="lg">
                    <Icon as={FaUsers} color="green.500" mb={2} />
                    <Text fontSize="2xl" fontWeight="bold" color="green.500">
                      {userStats?.totalClients || 0}
                    </Text>
                    <Text fontSize="sm" color={mutedText}>
                      Clients in org
                    </Text>
                  </Box>
                  <Box textAlign="center" p={4} bg={insetBg} borderRadius="lg">
                    <Icon as={FaProjectDiagram} color="purple.500" mb={2} />
                    <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                      {userStats?.totalProjects || 0}
                    </Text>
                    <Text fontSize="sm" color={mutedText}>
                      Total projects
                    </Text>
                  </Box>
                </SimpleGrid>
              </VStack>
            </SectionCard>

            <SectionCard title="Recent activity" icon={FaHistory}>
              {recentUserTasks.length > 0 ? (
                <VStack spacing={3} align="stretch">
                  {recentUserTasks.map((task) => (
                    <Box
                      key={task._id}
                      p={4}
                      bg={insetBg}
                      borderRadius="lg"
                      border="1px solid"
                      borderColor={borderColor}
                      _hover={{ borderColor: 'blue.300', cursor: 'pointer' }}
                      onClick={() => navigate(`/dashboard/task/${task._id}/details`)}
                      transition="all 0.2s"
                    >
                      <Flex justify="space-between" align="start" gap={4}>
                        <Box flex={1} minW={0}>
                          <Text fontWeight="semibold" noOfLines={1}>
                            {task.taskTitle || task.taskId}
                          </Text>
                          <Text fontSize="xs" color={mutedText} mt={1}>
                            {task.taskId}
                            {task.updatedAt &&
                              ` · Updated ${new Date(task.updatedAt).toLocaleDateString()}`}
                          </Text>
                          <HStack spacing={2} mt={2}>
                            <Badge colorScheme={getPriorityColor(task.priority)} borderRadius="full" fontSize="xs">
                              {task.priority}
                            </Badge>
                            <Badge colorScheme={getStatusColor(task.status)} borderRadius="full" fontSize="xs">
                              {task.status}
                            </Badge>
                          </HStack>
                        </Box>
                        <VStack align="end" spacing={1} flexShrink={0}>
                          <Text fontSize="sm" fontWeight="semibold" color="green.600">
                            {task.agreedPrice || task.quotedPrice
                              ? formatCurrency(task.agreedPrice || task.quotedPrice || 0)
                              : '—'}
                          </Text>
                          <Icon as={FaArrowRight} color="gray.400" boxSize={3} />
                        </VStack>
                      </Flex>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Box textAlign="center" py={10} color={mutedText}>
                  <Icon as={FaHistory} boxSize={10} mb={3} opacity={0.4} />
                  <Text>No recent task activity</Text>
                </Box>
              )}
            </SectionCard>
          </VStack>
        </GridItem>

        <GridItem position={{ xl: 'sticky' }} top={{ xl: 6 }}>
          <VStack spacing={4} align="stretch">
            <SectionCard title="Account details" icon={FaUserCog}>
              <VStack align="stretch" spacing={4} divider={<Divider />}>
                <Box>
                  <Text fontSize="xs" color={mutedText} textTransform="uppercase" letterSpacing="wide" mb={1}>
                    Full name
                  </Text>
                  <Text fontWeight="medium">
                    {authenticatedUser?.firstName} {authenticatedUser?.lastName}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color={mutedText} textTransform="uppercase" letterSpacing="wide" mb={1}>
                    Email
                  </Text>
                  <Text fontWeight="medium">{authenticatedUser?.email}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color={mutedText} textTransform="uppercase" letterSpacing="wide" mb={1}>
                    Role
                  </Text>
                  <Badge colorScheme={getRoleBadgeColor(authenticatedUser?.role || '')} borderRadius="full">
                    {authenticatedUser?.role?.toUpperCase()}
                  </Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color={mutedText} textTransform="uppercase" letterSpacing="wide" mb={1}>
                    User ID
                  </Text>
                  <Text fontSize="xs" fontFamily="mono" bg={insetBg} px={2} py={1} borderRadius="md" wordBreak="break-all">
                    {authenticatedUser?.LoggedInUserId}
                  </Text>
                </Box>
              </VStack>
            </SectionCard>

            <SectionCard title="Work summary" icon={FaFire}>
              <VStack align="stretch" spacing={4}>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <Icon as={FaCalendarDay} color={userStats?.overdueTasks ? 'red.500' : 'green.500'} />
                    <Text fontSize="sm">Overdue tasks</Text>
                  </HStack>
                  <Text fontWeight="bold" color={userStats?.overdueTasks ? 'red.500' : 'green.500'}>
                    {userStats?.overdueTasks || 0}
                  </Text>
                </Flex>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <Icon as={FaClock} color="purple.500" />
                    <Text fontSize="sm">Avg. completion</Text>
                  </HStack>
                  <Text fontWeight="bold">{userStats?.avgCompletionTime || 0} days</Text>
                </Flex>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <Icon as={FaCoins} color="green.500" />
                    <Text fontSize="sm">Task value</Text>
                  </HStack>
                  <Text fontWeight="bold" color="green.600">
                    {formatCurrency(userStats?.totalTaskValue || 0)}
                  </Text>
                </Flex>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <Icon as={FaFire} color="blue.500" />
                    <Text fontSize="sm">Activity (7 days)</Text>
                  </HStack>
                  <Text fontWeight="bold">{userStats?.recentActivity || 0}</Text>
                </Flex>
              </VStack>
            </SectionCard>

            <Button
              colorScheme="blue"
              variant="outline"
              borderRadius="full"
              onClick={() => navigate('/dashboard/tasks')}
            >
              View all tasks
            </Button>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default ProfileTab;
